import { type RefObject, useCallback, useEffect, useState } from "react";

/** How tall a list gets; the flip decides on this much room being there. */
const MENU_MAX_H = 320;

/** Hung below the anchor, or above it when there is no room below. */
type PlacedY = { top: number; bottom?: never } | { bottom: number; top?: never };
export type PopoverBox = { left: number; width: number } & PlacedY;

/**
 * Where to put a popover, in viewport coordinates.
 *
 * Positioned absolutely, a dropdown is clipped the moment its trigger sits
 * inside something that scrolls — the filters modal scrolls its body and hides
 * its overflow, so opening a list there swallowed the modal. A fixed element's
 * containing block is the viewport, which no ancestor's `overflow` can clip.
 *
 * Recomputed while open, on scroll (captured, so a scroll inside any container
 * counts) and on resize.
 */
export function usePopoverPosition(
  anchor: RefObject<HTMLElement | null>,
  open: boolean,
): PopoverBox {
  const [box, setBox] = useState<PopoverBox>({ left: 0, width: 0, top: 0 });

  const place = useCallback(() => {
    const rect = anchor.current?.getBoundingClientRect();
    if (!rect) return;
    const below = window.innerHeight - rect.bottom;
    const fits = below >= MENU_MAX_H + 8 || below >= rect.top;
    setBox({
      left: rect.left,
      width: rect.width,
      ...(fits ? { top: rect.bottom + 6 } : { bottom: window.innerHeight - rect.top + 6 }),
    });
  }, [anchor]);

  useEffect(() => {
    if (!open) return;
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, place]);

  return box;
}

/** How close a popover may come to the edge of the window. */
const EDGE = 8;

/** The inline style a popover wears; `min` keeps a narrow trigger readable. */
export function popoverStyle(box: PopoverBox, min = 224): React.CSSProperties {
  const width = Math.max(box.width, min);
  // `min` widens the list past its trigger, which on a narrow trigger near the
  // right edge hangs it off the screen — the pager's "25 / page" sits in that
  // corner on a phone, and its four choices opened into nothing. So the left
  // edge gives way instead: the list slides back onto the page and stays under
  // the control rather than starting under it.
  //
  // Recomputed on every placement, and `usePopoverPosition` re-places on resize
  // and scroll, so this follows a window that changes size while a list is open.
  const left = Math.max(EDGE, Math.min(box.left, window.innerWidth - width - EDGE));
  return {
    left,
    width,
    ...("top" in box ? { top: box.top } : { bottom: box.bottom }),
  };
}
